import { CalendarNext, CalendarPrev, CalendarToday, MbscCalendarEvent, MbscCalendarEventData, MbscResource } from '@mobiscroll/react';
import { Delete, Edit } from '@mui/icons-material';
import React from 'react';
import { IWorkplaces } from 'module/workplaces/interface/WorkplacesInterface';
import { useCalendarView } from 'core/hooks/useCalendarView';

import { ShiftIconComponent } from './components/ShiftIcon';
// buttons styles intentionally not used in this header
import { Button, IconButton } from '@mui/material';
import ScheduleWordIcon from "../../../../assets/images/SchedulerWordIcons.png"

interface ScheduleEventProps {
	calendarType: string;
	data: MbscCalendarEventData;
	onEditClick?: (event: MbscCalendarEvent) => void;
	onDeleteClick?: (event: MbscCalendarEvent) => void;
	canEditEvents?: boolean;
	canDeleteEvents?: boolean;
}

const getScheduleEventStyles = (original: MbscCalendarEvent) => {
	const dynamicStyle = original.added_by_mabel
		? { border: '2px solid var(--color-purple)' }
		: { borderColor: original?.background_color };

	return {
		cursor: 'pointer',
		backgroundColor: original?.background_color,
		color: original?.text_color || 'white', // Default to white if text_color not set
		...dynamicStyle,
	};
};

const getEventClassNames = (calendarType: string, original: MbscCalendarEventData['original']) => {
	return [
		'md-timeline-template-event',
		original?.overlap ? 'hasStripeBackground' : '',
		calendarType === 'request' ? 'requestsEventCustom' : '',
		!original?.role && calendarType === 'event' ? 'scheduleEvent' : '',
		original?.role === undefined && calendarType === 'request' ? 'timeoffColor' : '',
	]
		.filter(Boolean)
		.join(' ');
};
export const ScheduleEvent: React.FC<ScheduleEventProps> = ({
	calendarType,
	data,
	onEditClick,
	onDeleteClick,
	canEditEvents = true,
	canDeleteEvents = true,
}) => {
	const { original } = data!;

	const isRequest = original.role !== undefined || calendarType !== 'request';

	if (original?.shift_type?.id === 2 && original?.scheduled) return null;

	const handleEditClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent event from triggering calendar's onEventClick
		if (!canEditEvents) {
			return;
		}
		if (onEditClick && original) {
			onEditClick(original);
		}
	};

	const handleDeleteClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent event from triggering calendar's onEventClick
		if (!canDeleteEvents) {
			return;
		}
		if (onDeleteClick && original) {
			onDeleteClick(original);
		}
	};

	// Extract event name only (remove comma and anything after it)
	const getEventNameOnly = (name: string | undefined) => {
		if (!name) return '';
		// Remove comma and everything after it
		const nameOnly = name.split(',')[0].trim();
		return nameOnly;
	};

	// Format time for display
	const formatTime = (time: string) => {
		if (!time) return '';
		// Extract just the time part (HH:mm format)
		const timeMatch = time.match(/(\d{2}:\d{2})/);
		return timeMatch ? timeMatch[1] : time;
	};

	// Calculate event duration in hours
	const getEventDuration = (start: any, end: any) => {
		if (!start || !end) return 0;
		
		// Handle both Date objects and strings
		const startTime = start instanceof Date ? start.getTime() : new Date(start).getTime();
		const endTime = end instanceof Date ? end.getTime() : new Date(end).getTime();
		
		// Check if dates are valid
		if (isNaN(startTime) || isNaN(endTime)) return 0;
		
		const durationMs = endTime - startTime;
		const durationHours = durationMs / (1000 * 60 * 60); // Convert to hours
		return durationHours;
	};

	// Get formatted event name based on duration
	const getFormattedEventName = (name: string | undefined, start: any, end: any) => {
		const eventName = getEventNameOnly(name);
		if (!eventName) return '';
		
		const duration = getEventDuration(start, end);
		
		// If duration is less than 1 hour (e.g., 45 minutes = 0.75 hours), truncate to 10 characters
		if (duration > 0 && duration < 1) {
			if (eventName.length > 10) {
				return eventName.substring(0, 5) + '..';
			}
		}
		
		return eventName;
	};

	return (
		<div
			className={getEventClassNames(calendarType, original)}
			style={getScheduleEventStyles(original)}
		>
			<div className="md-custom-event-wrapper">
				<div className="md-custom-event-details">
					{/* Event name on top - bold and larger font */}
					<div className="md-custom-event-title">
						{calendarType === 'event' ? (
							<span className="event-name-text">
								{getFormattedEventName(original?.name, original?.start, original?.end)}
							</span>
						) : isRequest ? (
							<span>
								{data.start} - {data.end}
							</span>
						) : (
							'Time off'
						)}
						<span className="event-action-buttons">
							<IconButton
								onClick={handleEditClick}
								size="small"
								className="edit-btn"
								disabled={!canEditEvents}
								style={{
									backgroundColor: 'white',
									color: 'rgb(68, 99, 136)',
									cursor: canEditEvents ? 'pointer' : 'not-allowed',
									opacity: canEditEvents ? 1 : 0.5,
								}}
							>
								<Edit fontSize="small" />
							</IconButton>
							<IconButton
								onClick={handleDeleteClick}
								size="small"
								className="delete-btn"
								disabled={!canDeleteEvents}
								style={{
									backgroundColor: 'white',
									color: '#d32f2f',
									cursor: canDeleteEvents ? 'pointer' : 'not-allowed',
									opacity: canDeleteEvents ? 1 : 0.5,
								}}
							>
								<Delete fontSize="small" />
							</IconButton>
						</span>
					</div>
				</div>
				{/* Time at bottom - bold */}
				<div className="md-timeline-template-role">
					{calendarType === 'event' ? (
						<span className="event-time-text">
							{formatTime(data.start)} - {formatTime(data.end)}
						</span>
					) : (
						<span>{original?.role ?? 'No Role'}</span>
					)}
					<ShiftIconComponent shift={original} />
				</div>
			</div>
		</div>
	);
};

interface ScheduleResourceProps {
	resource: MbscResource;
	calendarType: string;
}

export const ScheduleResource: React.FC<ScheduleResourceProps> = ({ resource, calendarType }) => {
	// Hide resource labels for dynamic row resources (those with '-row-' in the ID)
	const isRowResource = typeof resource.id === 'string' && resource.id.includes('-row-');
	
	// For event calendar, show blank resource column (similar to Schedule)
	if (calendarType === 'event') {
		return (
			<div className={`md-work-week-cont`} style={{ 
				height: '100%',
				minHeight: '100%',
				display: 'flex',
				alignItems: 'stretch'
			}}>
				<div className="md-work-week-name" style={{ 
					visibility: 'hidden',
					height: '100%',
					minHeight: '100%',
					width: '100%'
				}}>
					{/* Blank column - no text displayed, similar to Schedule first column */}
				</div>
			</div>
		);
	}
	
	return (
		<div className={`md-work-week-cont`}>
			<div className="md-work-week-name">
				{calendarType === 'event'
					? resource.name
					: `${resource.name} (${resource?.active_week_hours ?? 0})`}
			</div>
			{/* Role/description intentionally removed per new UI requirements */}
		</div>
	);
};

interface MyHeaderProps {
	calendarType: string;
	setCalendarType: (args: 'schedule' | 'request' | 'event') => void;
	workplacesData?: IWorkplaces;
	workplaceId: number;
}

export const CalendarHeader: React.FC<MyHeaderProps> = ({
	calendarType,
	setCalendarType,
	workplacesData,
	workplaceId,
}) => {
	const { calendarView, setCalendarView } = useCalendarView();

	const workplaceName =
		workplacesData?.items?.find((workplace) => workplaceId === +workplace.id)?.name || '';

	return (
		<>
			<div className="customHeader">
				
				<div className="customHeaderBottom">
					

					<div className="image-container">
						<img
							src={ScheduleWordIcon}
							style={{ width: '170px' }}
							alt=""
							onClick={() => setCalendarType('schedule')}
						/>
						<div className="workplaceLabel" style={{ marginLeft: 12 }}>
							<span className="WorkplaceName">{workplaceName}</span>
							<span className="calendarTypeLabel" style={{ marginLeft: 8, opacity: 0.8 }}>
								{calendarType}
							</span>
						</div>
					</div>

					<div style={{ display: 'flex', alignItems: 'end', justifyContent: 'end', width: '100%' }}>
						<Button
							style={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: 'white',
								padding: '5px 12px',
							}}
						>
							Publish
						</Button>
						<div className="viewToggleContainer" style={{ marginLeft: 12, display: 'flex', gap: 8 }}>
							<button
								className={`viewToggle ${calendarView === 'day' ? 'active' : ''}`}
								onClick={() => setCalendarView('day')}
							>
								Day
							</button>
							<button
								className={`viewToggle ${calendarView === 'week' ? 'active' : ''}`}
								onClick={() => setCalendarView('week')}
							>
								Week
							</button>
						</div>
						<CalendarPrev className="md-work-week-prev" />
						<CalendarToday className="md-work-week-today" />
						<CalendarNext className="md-work-week-next" />
					</div>
				</div>
			</div>
		</>
	);
};
